import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ExternalLink } from "lucide-react";

interface Platform {
  name: string;
  status: "live" | "scheduled" | "inactive";
  viewers?: string;
  scheduledTime?: string;
  title: string;
  streamKey?: string;
  page?: string;
  account?: string;
  channel?: string;
  quality: string;
  visibility?: string;
  color: string;
  textColor: string;
  bgColor: string;
  logo: React.ReactNode;
}

interface PlatformCardProps {
  platform: Platform;
}

export function PlatformCard({ platform }: PlatformCardProps) {
  const getStatusBadge = () => {
    switch (platform.status) {
      case "live":
        return (
          <div className="status-badge text-xs font-medium text-accent">
            <span className="live-indicator w-2 h-2 bg-accent rounded-full"></span>
            LIVE
          </div>
        );
      case "scheduled":
        return (
          <Badge variant="secondary" className="text-xs font-medium">
            Scheduled
          </Badge>
        );
      case "inactive":
        return (
          <Badge variant="outline" className="text-xs font-medium">
            Inactive
          </Badge>
        );
    }
  };

  const getGradientClass = () => {
    if (platform.name === "YouTube Live") return "from-red-50 to-white";
    if (platform.name === "Facebook Live") return "from-blue-50 to-white";
    if (platform.name === "X (formerly Twitter)") return "from-gray-50 to-white";
    if (platform.name === "Twitch") return "from-purple-50 to-white";
    return "from-gray-50 to-white";
  };

  return (
    <Card className="platform-card overflow-hidden" data-testid={`card-platform-${platform.name.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className={`p-6 border-b border-border bg-gradient-to-r ${getGradientClass()}`}>
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className={`w-12 h-12 ${platform.color} rounded-lg flex items-center justify-center`}>
              {platform.logo}
            </div>
            <div>
              <h4 className="text-lg font-semibold">{platform.name}</h4>
              <div className="flex items-center gap-2 mt-1">
                {getStatusBadge()}
                {platform.viewers && (
                  <span className="text-xs text-muted-foreground" data-testid={`text-${platform.name.toLowerCase().replace(/\s+/g, '-')}-viewers`}>
                    {platform.viewers} viewers
                  </span>
                )}
                {platform.scheduledTime && (
                  <span className="text-xs text-muted-foreground">{platform.scheduledTime}</span>
                )}
              </div>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="text-muted-foreground hover:text-foreground"
            data-testid={`button-${platform.name.toLowerCase().replace(/\s+/g, '-')}-details`}
          >
            <ExternalLink className="w-5 h-5" />
          </Button>
        </div>
      </div>
      <CardContent className="p-6">
        <div className="space-y-3">
          <div>
            <p className="text-xs text-muted-foreground mb-1">Stream Title</p>
            <p className="font-medium">{platform.title}</p>
          </div>
          {platform.streamKey && (
            <div>
              <p className="text-xs text-muted-foreground mb-1">Stream Key</p>
              <code className="text-xs bg-muted px-2 py-1 rounded">{platform.streamKey}</code>
            </div>
          )}
          {platform.page && (
            <div>
              <p className="text-xs text-muted-foreground mb-1">Page</p>
              <p className="text-sm">{platform.page}</p>
            </div>
          )}
          {platform.account && (
            <div>
              <p className="text-xs text-muted-foreground mb-1">Account</p>
              <p className="text-sm">{platform.account}</p>
            </div>
          )}
          {platform.channel && (
            <div>
              <p className="text-xs text-muted-foreground mb-1">Channel</p>
              <p className="text-sm">{platform.channel}</p>
            </div>
          )}
          <div className="flex items-center justify-between pt-2">
            <div className="flex gap-2">
              <Badge className={`text-xs ${platform.bgColor} ${platform.textColor} font-medium`}>
                {platform.quality}
              </Badge>
              {platform.visibility && (
                <Badge className={`text-xs ${platform.bgColor} ${platform.textColor} font-medium`}>
                  {platform.visibility}
                </Badge>
              )}
            </div>
            <Button
              variant="link"
              size="sm"
              className="text-sm text-primary hover:text-primary/80 font-medium p-0"
              data-testid={`button-${platform.name.toLowerCase().replace(/\s+/g, '-')}-manage`}
            >
              Manage →
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
